import React, { useState, useCallback } from 'react';
import { 
  Upload, Download, FileSpreadsheet, CheckCircle, XCircle, 
  AlertCircle, Eye, Database, Users, Plane, Warehouse,
  Loader2, RefreshCw, Trash2, ChevronRight, FileWarning
} from 'lucide-react';

const ImportWizard = () => {
  const [selectedTable, setSelectedTable] = useState('owners');
  const [file, setFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: select, 2: upload, 3: preview, 4: confirm
  const [onDuplicate, setOnDuplicate] = useState('skip');
  const [error, setError] = useState(null);

  const tables = [
    { id: 'owners', label: 'Owners (Clients)', icon: Users, color: 'text-green-400', description: 'Import client data' },
    { id: 'aircraft', label: 'Aircraft', icon: Plane, color: 'text-blue-400', description: 'Import aircraft fleet' },
    { id: 'hangars', label: 'Hangars', icon: Warehouse, color: 'text-yellow-400', description: 'Import hangar inventory' }
  ];

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(`${API_URL}/import/template/${selectedTable}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedTable}_template.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download template');
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validExtensions = ['.xlsx', '.xls', '.csv'];
      const extension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.'));
      if (!validExtensions.includes(extension.toLowerCase())) {
        setError('Please upload an Excel (.xlsx, .xls) or CSV file');
        return;
      }
      setFile(selectedFile);
      setError(null);
      setStep(2);
    }
  };

  const handleValidate = async () => {
    if (!file) return;
    
    setLoading(true);
    setError(null);
    
    const formData = new FormData();
    formData.append('table_type', selectedTable);
    formData.append('file', file);
    
    try {
      const response = await fetch(`${API_URL}/import/validate`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setValidationResult(data);
        setStep(3);
      } else {
        setError(data.detail || 'Validation failed');
      }
    } catch (err) {
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!validationResult || !validationResult.data) return;
    
    setLoading(true);
    setError(null);
    
    const formData = new FormData();
    formData.append('table_type', selectedTable);
    formData.append('on_duplicate', onDuplicate);
    formData.append('data', JSON.stringify(validationResult.data));
    
    try {
      const response = await fetch(`${API_URL}/import/execute`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setImportResult(data);
        setStep(4);
      } else {
        setError(data.detail || 'Import failed');
      }
    } catch (err) {
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const resetWizard = () => {
    setStep(1);
    setFile(null);
    setValidationResult(null);
    setImportResult(null);
    setError(null);
  };

  const currentTable = tables.find(t => t.id === selectedTable);
  const Icon = currentTable?.icon;

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-400" />
              Data Import Wizard
            </h3>
            <p className="text-gray-400 text-sm mt-1">
              Import owners, aircraft, or hangars from Excel/CSV files
            </p>
          </div>
          {step > 1 && (
            <button
              onClick={resetWizard}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 hover:text-white transition"
            >
              <RefreshCw className="w-4 h-4" />
              New Import
            </button>
          )}
        </div>
      </div>

      <div className="p-6">
        {/* Step 1: Select Table */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Select data type to import
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {tables.map((table) => (
                  <button
                    key={table.id}
                    onClick={() => setSelectedTable(table.id)}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      selectedTable === table.id
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`p-2 rounded-lg bg-gray-700/50 ${table.color}`}>
                        <table.icon className="w-5 h-5" />
                      </div>
                      <span className="font-medium text-white">{table.label}</span>
                    </div>
                    <p className="text-xs text-gray-500">{table.description}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setStep(2)}
                className="flex items-center gap-2 px-6 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white transition"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Upload File */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg border border-gray-700">
              <div>
                <p className="text-white font-medium">
                  Importing: <span className="text-blue-400">{currentTable?.label}</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Download the template to ensure correct column format
                </p>
              </div>
              <button
                onClick={handleDownloadTemplate}
                className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm transition"
              >
                <Download className="w-4 h-4" />
                Download Template
              </button>
            </div>

            <div className="border-2 border-dashed border-gray-700 rounded-xl p-8 text-center hover:border-blue-500 transition">
              <input
                type="file"
                id="file-upload"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
                className="hidden"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center gap-3"
              >
                <FileSpreadsheet className="w-12 h-12 text-gray-500" />
                <div>
                  <p className="text-white font-medium">
                    {file ? file.name : 'Click to upload or drag and drop'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Supported formats: .xlsx, .xls, .csv
                  </p>
                </div>
              </label>
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <div className="flex justify-between">
              <button
                onClick={() => setStep(1)}
                className="px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition"
              >
                Back
              </button>
              <button
                onClick={handleValidate}
                disabled={!file || loading}
                className="flex items-center gap-2 px-6 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white transition disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                Validate & Preview
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Preview & Confirm */}
        {step === 3 && validationResult && (
          <div className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                <div className="flex items-center gap-2 text-green-400">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">Valid Rows</span>
                </div>
                <p className="text-2xl font-bold text-white mt-1">{validationResult.valid_rows}</p>
                <p className="text-xs text-gray-500">out of {validationResult.total_rows} total</p>
              </div>
              <div className="p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                <div className="flex items-center gap-2 text-yellow-400">
                  <FileWarning className="w-4 h-4" />
                  <span className="text-sm font-medium">Errors</span>
                </div>
                <p className="text-2xl font-bold text-white mt-1">{validationResult.error_rows}</p>
                <p className="text-xs text-gray-500">rows with validation errors</p>
              </div>
              <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                <div className="flex items-center gap-2 text-blue-400">
                  <Database className="w-4 h-4" />
                  <span className="text-sm font-medium">On Duplicate</span>
                </div>
                <select
                  value={onDuplicate}
                  onChange={(e) => setOnDuplicate(e.target.value)}
                  className="mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg px-2 py-1 text-white text-sm"
                >
                  <option value="skip">Skip duplicates</option>
                  <option value="update">Update existing</option>
                </select>
              </div>
            </div>

            {/* Data Preview */}
            {validationResult.preview && validationResult.preview.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-300 mb-3">Data Preview (first 5 rows)</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-900/50">
                      <tr>
                        {Object.keys(validationResult.preview[0] || {}).map((col) => (
                          <th key={col} className="px-3 py-2 text-left text-gray-400 font-medium">
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {validationResult.preview.map((row, idx) => (
                        <tr key={idx} className="border-t border-gray-800">
                          {Object.values(row).map((val, colIdx) => (
                            <td key={colIdx} className="px-3 py-2 text-gray-300">
                              {val !== null && val !== undefined ? String(val).substring(0, 50) : '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Errors List */}
            {validationResult.errors && validationResult.errors.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-red-400 mb-3">Validation Errors</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {validationResult.errors.slice(0, 10).map((err, idx) => (
                    <div key={idx} className="p-2 bg-red-500/10 rounded-lg text-sm">
                      <span className="text-red-400 font-medium">Row {err.row}:</span>
                      <span className="text-gray-400 ml-2">{err.errors.join(', ')}</span>
                    </div>
                  ))}
                  {validationResult.errors.length > 10 && (
                    <p className="text-xs text-gray-500">... and {validationResult.errors.length - 10} more errors</p>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <button
                onClick={() => setStep(2)}
                className="px-6 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition"
              >
                Back
              </button>
              <button
                onClick={handleImport}
                disabled={loading || validationResult.valid_rows === 0}
                className="flex items-center gap-2 px-6 py-2 bg-green-500 hover:bg-green-600 rounded-lg text-white transition disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Confirm Import ({validationResult.valid_rows} rows)
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Results */}
        {step === 4 && importResult && (
          <div className="space-y-6">
            <div className="text-center p-6">
              <div className={`inline-flex p-3 rounded-full ${
                importResult.failed === 0 ? 'bg-green-500/20' : 'bg-yellow-500/20'
              } mb-4`}>
                {importResult.failed === 0 ? (
                  <CheckCircle className="w-8 h-8 text-green-400" />
                ) : (
                  <AlertCircle className="w-8 h-8 text-yellow-400" />
                )}
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Import Completed</h3>
              <div className="flex justify-center gap-6 mt-4">
                <div>
                  <p className="text-2xl font-bold text-green-400">{importResult.imported}</p>
                  <p className="text-xs text-gray-500">Imported</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-400">{importResult.skipped || 0}</p>
                  <p className="text-xs text-gray-500">Skipped</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-400">{importResult.failed}</p>
                  <p className="text-xs text-gray-500">Failed</p>
                </div>
              </div>
            </div>

            {importResult.errors && importResult.errors.length > 0 && (
              <div className="max-h-60 overflow-y-auto">
                <h4 className="text-sm font-medium text-red-400 mb-2">Failed Records</h4>
                {importResult.errors.slice(0, 10).map((err, idx) => (
                  <div key={idx} className="p-2 bg-red-500/10 rounded-lg text-sm mb-1">
                    <span className="text-red-400">Error:</span>
                    <span className="text-gray-400 ml-2">{err.error}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-center">
              <button
                onClick={resetWizard}
                className="px-6 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white transition"
              >
                Import Another File
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportWizard;